import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { Form, Formik } from "formik";
import * as yup from "yup";
import React from "react";
import { useRenameZahllauf } from "../../client/hooks";
import { useMutation, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { useT } from "../../i18n";
import EditIcon from "@mui/icons-material/Edit";
import ZahllaufModalFields from "./ZahllaufModalFields";
import { useZahllaufContext } from "../../contexts/ZahllaufContext";

export default function UpdateZahllaufUmbennenButton({
  zahllaufId,
  setZahllaufId,
}: {
  zahllaufId: string | undefined;
  setZahllaufId: (value: string) => void;
}) {
  const { t } = useT();
  const { t: t1 } = useT("zahllauf");
  const zahllaufContext = useZahllaufContext();

  const mutate = useRenameZahllaufMutation();

  const popupState = usePopupState({
    variant: "popover",
    popupId: `zahllauf-${zahllaufId}-umbennen`,
  });
  const schema = yup.object().shape({
    bezeichnung: yup.string().required(),
  });

  const onSubmit = async (values: { bezeichnung: string }) => {
    if (!zahllaufId) return;

    try {
      await mutate.mutateAsync({
        zahllaufId,
        bezeichnung: values.bezeichnung,
      });

      // Update inputValue immediately with the new bezeichnung
      if (
        zahllaufContext.inputValue &&
        typeof zahllaufContext.inputValue === "object" &&
        "bezeichnung" in zahllaufContext.inputValue
      ) {
        zahllaufContext.setInputValue({
          ...zahllaufContext.inputValue,
          bezeichnung: values.bezeichnung,
        });
      }

      setZahllaufId(zahllaufId);

      // Set the selected zahllauf ID in context
      zahllaufContext.setSelectedZahllaufId(zahllaufId);

      // Invalidate the current zahllauf query to reload with new name
      zahllaufContext.invalidateCurrentZahllauf();

      // Trigger a refresh by updating the search value
      zahllaufContext.setSearchValue(values.bezeichnung);

      popupState.close();
      enqueueSnackbar(t1("paymentrun-renamed"), {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(t1("paymentrun-renamed-error"), {
        variant: "error",
      });
    }
  };
  return (
    <>
      <IconButton
        {...bindTrigger(popupState)}
        aria-label="edit"
        sx={{ color: "black" }}
      >
        <EditIcon />
      </IconButton>
      <Dialog {...bindPopover(popupState)}>
        <DialogTitle id="alert-dialog-title">
          {t1("paymentrun-rename")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Formik
              enableReinitialize={true}
              validationSchema={schema}
              initialValues={{
                bezeichnung: "",
              }}
              onSubmit={onSubmit}
            >
              {({ isSubmitting, isValid, dirty }) => {
                return (
                  <>
                    <Form id="rename-zahllauf">
                      <ZahllaufModalFields zahllaufId={zahllaufId} />
                      <DialogActions>
                        <Button
                          onClick={() => popupState.close()}
                          color="inherit"
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          type="submit"
                          color="primary"
                          variant={"contained"}
                          disabled={isSubmitting || !dirty || !isValid}
                        >
                          {t1("rename")}
                        </Button>
                      </DialogActions>
                    </Form>
                  </>
                );
              }}
            </Formik>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
}
function useRenameZahllaufMutation() {
  const renameZahllauf = useRenameZahllauf();
  const queryClient = useQueryClient();
  const { incrementServerKeyVersion } = useZahllaufContext();

  return useMutation(
    (input: { zahllaufId: string; bezeichnung: string }) => {
      return renameZahllauf(input.zahllaufId, {
        bezeichnung: input.bezeichnung,
      });
    },
    {
      onSuccess: async (data, variables) => {
        // Update the cached data directly with the new bezeichnung
        queryClient.setQueryData(["zahllaeufe"], (oldData: any) => {
          if (oldData) {
            return {
              ...oldData,
              bezeichnung: variables.bezeichnung,
            };
          }
          return oldData;
        });

        queryClient.invalidateQueries({
          queryKey: ["zahllauf"],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ["zahllaeufe"],
          exact: false,
        });
        incrementServerKeyVersion();
      },
      onError: (error) => {
        console.error("Failed to rename Zahllauf:", error);
      },
    }
  );
}
